// @ts-nocheck
import React from 'react';
import { Routes, Route, Navigate, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginPage from '../components/common/LoginPage';
import GlobalNavigation from '../components/common/GlobalNavigation';
import MinimalAppHeader from '../components/common/MinimalAppHeader';
import CreditManagementApp from '../pages/credit-management/CreditManagementApp';
import CyberSweepApp from '../pages/cybersweep/CyberSweepApp';
import CreditBlockApp from '../pages/credit-block/CreditBlockApp';
import UserManagement from '../pages/admin/UserManagement';
import UIConfigurationManager from '../pages/admin/UIConfigurationManager';
import UserProfile from '../components/common/UserProfile';
import Settings from '../components/common/Settings';
import SystemAdmin from '../components/common/SystemAdmin';
import SupportTickets from '../components/common/SupportTickets';
import DynamicFormSectionTest from '../pages/test/DynamicFormSectionTest';
import { ClientListView } from '../components/lists/ClientListView';
import { LeadListView } from '../components/lists/LeadListView';
import { ContactListView } from '../components/lists/ContactListView';
import { OpportunityListView } from '../components/lists/OpportunityListView';
import { CaseListView } from '../components/lists/CaseListView';
import { EventListView } from '../components/lists/EventListView';
import { ReportListView } from '../components/lists/ReportListView';
import { TaskListView } from '../components/lists/TaskListView';
import { ProductListView } from '../components/lists/ProductListView';
import { PricebookListView } from '../components/lists/PricebookListView';
import { AccountListView } from '../components/lists/AccountListView';
import { InvoiceListView } from '../components/lists/InvoiceListView';
import { LeadDetailPage } from '../pages/detail/LeadDetailPage';
import { ClientDetailPage } from '../pages/detail/ClientDetailPage';
import { OpportunityDetailPage } from '../pages/detail/OpportunityDetailPage';
import { ContactDetailPage } from '../pages/detail/ContactDetailPage';
import { CaseDetailPage } from '../pages/detail/CaseDetailPage';
import { EventDetailPage } from '../pages/detail/EventDetailPage';
import { TaskDetailPage } from '../pages/detail/TaskDetailPage';
import { ReportDetailPage } from '../pages/detail/ReportDetailPage';
import { ProductDetailPage } from '../pages/detail/ProductDetailPage';
import { PricebookDetailPage } from '../pages/detail/PricebookDetailPage';
import { AccountDetailPage } from '../pages/detail/AccountDetailPage';
import { InvoiceDetailPage } from '../pages/detail/InvoiceDetailPage';
import { KeyboardShortcutsModal } from '../components/common/KeyboardShortcutsModal';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useSwitchUser } from '../contexts/SwitchUserContext';
import CustomerServiceChatbot from '../components/common/CustomerServiceChatbot';
import { persistedClientRecords } from '../services/mockData/clients';
import { persistedTaskRecords } from '../services/mockData/tasks';
import { getMVPDefaultRole, shouldBypassRoleChecks } from '../config/mvpConfig';
import { useCanAccessApp, useCurrentUser as usePermissionCurrentUser } from '../services/queries/permissionQueries';
import { getAppViewRole } from '../services/rbac/rolePermissionSummary';
import PermissionDebugPanel from '../components/common/PermissionDebugPanel';
import { ShieldAlert } from 'lucide-react';
import HomePage from '../pages/HomePage';
import DashboardPage from '../pages/DashboardPage';
import AdminPage from '../pages/AdminPage';

const AccessDenied: React.FC<{ appName: string }> = ({ appName }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You do not have permission to access <span className="font-medium">{appName}</span>.
          Contact your administrator to request access.
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return Home
        </button>
      </div>
    </div>
  );
};

const NoRoleAssigned: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="mb-6">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-10 h-10 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">No Role Assigned</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your account is authenticated but does not have a Gobodo role assignment yet.
          Ask an administrator to assign at least one role before testing RBAC.
        </p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Return Home
        </button>
      </div>
    </div>
  );
};

interface RouteWrapperProps {
  children: React.ReactNode;
  showMainNavigation?: boolean;
  currentView?: string;
  useMinimalHeader?: boolean;
  appTitle?: string;
}

const RouteWrapper: React.FC<RouteWrapperProps> = ({
  children,
  showMainNavigation = true,
  currentView,
  useMinimalHeader = false,
  appTitle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const { data: permissionUser } = usePermissionCurrentUser();
  
  const getUserRole = (): string => {
    if (permissionUser) {
      return getAppViewRole('regal_main', permissionUser.roles, permissionUser.keycloakRoles);
    }
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  const { isSwitched } = useSwitchUser();
  const { isShortcutsModalOpen, setIsShortcutsModalOpen } = useKeyboardShortcuts();
  
  const contentPadding = isSwitched ? 'pt-[108px]' : 'pt-16';
  const contentPadding20 = isSwitched ? 'pt-[124px]' : 'pt-20';
  const contentPadding24 = isSwitched ? 'pt-[140px]' : 'pt-24';

  const [userRole, setUserRole] = React.useState(getUserRole());

  React.useEffect(() => {
    if (user) {
      const newRole = getUserRole();
      setUserRole(newRole);
    }
  }, [user, hasRole]);

  const handleAppSelect = (app: any) => {
    if (app.name === 'home') {
      navigate('/');
    } else if (app.name?.toLowerCase().includes('system') && app.name?.toLowerCase().includes('admin')) {
      navigate('/admin');
    } else {
      navigate(`/${app.name.toLowerCase().replace(' ', '-')}`);
    }
  };

  const handleNavigateToTask = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleNavigateToCase = (caseId: string) => {
    navigate(`/cases/${caseId}`);
  };

  // Determine current view from location pathname
  const getCurrentViewFromPath = () => {
    if (currentView) return currentView;
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/admin' || path.startsWith('/admin')) return 'system-admin';
    if (path.startsWith('/credit-management')) return 'credit-management';
    if (path.startsWith('/cybersweep')) return 'app-cybersweep';
    if (path.startsWith('/credit-block')) return 'app-credit-block';
    return path.replace('/', '').split('/')[0];
  };

  const handleNavigate = (view: string) => {
    if (view === 'home') {
      navigate('/');
    } else if (view.startsWith('app-')) {
      navigate(`/${view.replace('app-', '')}`);
    } else if (view === 'system-admin') {
      navigate('/admin');
    } else {
      navigate(`/${view}`);
    }
  };

  return (
    <>
      {useMinimalHeader ? (
        <MinimalAppHeader
          appTitle={appTitle ?? ''}
          currentView={getCurrentViewFromPath()}
          onAppSelect={handleAppSelect}
          onBackToHome={() => navigate('/')}
          onNavigate={handleNavigate}
          userRole={userRole}
        />
      ) : (
        <GlobalNavigation
          currentView={getCurrentViewFromPath()}
          onNavigate={handleNavigate}
          userRole={userRole}
          onRoleChange={setUserRole}
          showMainNavigation={showMainNavigation}
          onAppSelect={handleAppSelect}
          onNavigateToTask={handleNavigateToTask}
          onNavigateToCase={handleNavigateToCase}
        />
      )}
      <div
        className={
          useMinimalHeader ? contentPadding : showMainNavigation ? contentPadding24 : contentPadding
        }
      >
        {children}
      </div>
      <PermissionDebugPanel />
      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
      />
    </>
  );
};

export const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();
  
  const getUserRole = (): string => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };

  React.useEffect(() => {
    try {
      const { addCreditScoreFieldsToClient } = require('../utils/addCreditScoreFields');
      addCreditScoreFieldsToClient();
      const { USE_GOBODO_API } = require('../utils/gobodoUtils');
      if (process.env.NODE_ENV === 'development') {
        console.log('[Gobodo] REACT_APP_USE_GOBODO_API=', process.env.REACT_APP_USE_GOBODO_API, 'USE_GOBODO_API=', USE_GOBODO_API);
      }
      if (USE_GOBODO_API) {
        const processAutomationKeys = [
          'crm_approval_processes',
          'crm_processes',
          'crm_process_executions',
          'crm_flows',
          'crm_flow_runs',
          'crm_flow_versions',
          'crm_scheduled_jobs',
          'crm_job_executions'
        ];
        processAutomationKeys.forEach(key => localStorage.removeItem(key));
      }
      if (!USE_GOBODO_API) {
        const { initializeMockWorkflowRules } = require('../data/mockWorkflowRules');
        const { initializeApprovalProcessData } = require('../data/mockApprovalProcesses');
        initializeMockWorkflowRules();
        initializeApprovalProcessData();
      }
    } catch (error) {
      console.error('Initialization error:', error);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Role-based home redirect — CB/CS roles go straight to their app
  const RoleBasedHome: React.FC = () => {
    const { data: pu, isLoading: permLoading } = usePermissionCurrentUser();
    const { user: authUser, hasRole: authHasRole } = useAuth();

    const primaryApp = React.useMemo(() => {
      // Check Gobodo roles first
      if (pu?.roles?.length) {
        const roleNames = pu.roles.map((r: any) => (r.name || '').toLowerCase());
        const hasAdmin = roleNames.some((n: string) => n.includes('system administrator') || n.includes('operations manager'));
        if (hasAdmin) return null; // admins/managers see full home
        const hasCB = roleNames.some((n: string) => n.includes('credit block'));
        if (hasCB) return '/credit-block/overview';
        const hasCS = roleNames.some((n: string) => n.includes('cybersweep'));
        if (hasCS) return '/cybersweep';
      }
      // Fall back to Keycloak roles
      if (authUser) {
        if (authHasRole('system_admin') || authHasRole('manager')) return null;
        if (authHasRole('cb_supervisor') || authHasRole('cb_team')) return '/credit-block/overview';
        if (authHasRole('cs_supervisor') || authHasRole('cs_auditor')) return '/cybersweep';
      }
      return null;
    }, [pu, authUser, authHasRole]);

    // Show loading spinner while roles are being fetched to prevent flash
    if (permLoading) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      );
    }

    if (primaryApp) return <Navigate to={primaryApp} replace />;
    return <HomePage />;
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<RoleBasedHome />} />
        <Route path="/credit-management" element={<CreditManagementRoute />} />
        <Route path="/cybersweep" element={<CyberSweepRoute />} />
        <Route path="/cybersweep/client/:clientId" element={<CyberSweepRoute />} />
        <Route path="/cybersweep/client/:clientId/audit/:auditId" element={<CyberSweepRoute />} />
        <Route path="/cybersweep/audit-templates" element={<CyberSweepRoute routeView="audit-templates" />} />
        <Route path="/cybersweep/audit-templates/:templateId" element={<CyberSweepRoute routeView="audit-template-detail" />} />
        <Route path="/cybersweep/website-configuration" element={<CyberSweepRoute routeView="website-configuration" />} />
        <Route path="/cybersweep/website-configuration/:websiteId" element={<CyberSweepRoute routeView="website-configuration-detail" />} />
        <Route path="/cybersweep/audit/:auditId" element={<CyberSweepRoute />} />
        <Route path="/credit-block" element={<Navigate to="/credit-block/overview" replace />} />
        <Route path="/credit-block/client/:clientId/:clientSection" element={<CreditBlockRoute />} />
        <Route path="/credit-block/client/:clientId" element={<CreditBlockRoute />} />
        <Route path="/credit-block/case/:caseId" element={<CreditBlockRoute />} />
        <Route path="/credit-block/task/:taskId" element={<CreditBlockRoute />} />
        <Route path="/credit-block/:tab" element={<CreditBlockRoute />} />
        <Route path="/user-management" element={<UserManagementRoute />} />
        <Route path="/ui-configuration" element={<UIConfigurationRoute />} />
        <Route path="/dynamic-form-test" element={<DynamicFormTestRoute />} />
        <Route path="/profile" element={<ProfileRoute />} />
      <Route path="/settings" element={<SettingsRoute />} />
      <Route path="/admin/*" element={<AdminRoute />} />
      <Route path="/support-tickets" element={<SupportTicketsRoute />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/clients" element={<ClientsRoute />} />
      <Route path="/clients/:id" element={<ClientDetailRoute />} />
      <Route path="/leads" element={<LeadsRoute />} />
      <Route path="/leads/:id" element={<LeadDetailRoute />} />
      <Route path="/opportunities" element={<OpportunitiesRoute />} />
      <Route path="/opportunities/:id" element={<OpportunityDetailRoute />} />
      <Route path="/contacts" element={<ContactsRoute />} />
      <Route path="/contacts/:id" element={<ContactDetailRoute />} />
      <Route path="/accounts" element={<AccountsRoute />} />
      <Route path="/accounts/:id" element={<AccountDetailRoute />} />
      <Route path="/cases" element={<CasesRoute />} />
      <Route path="/cases/:id" element={<CaseDetailRoute />} />
      <Route path="/events" element={<EventsRoute />} />
      <Route path="/events/:id" element={<EventDetailRoute />} />
      <Route path="/reports" element={<ReportsRoute />} />
      <Route path="/reports/:id" element={<ReportDetailRoute />} />
      <Route path="/tasks" element={<TasksRoute />} />
      <Route path="/tasks/:id" element={<TaskDetailRoute />} />
      <Route path="/products" element={<ProductsRoute />} />
      <Route path="/products/:id" element={<ProductDetailRoute />} />
      <Route path="/pricebooks" element={<PricebooksRoute />} />
      <Route path="/pricebooks/:id" element={<PricebookDetailRoute />} />
      <Route path="/invoices" element={<InvoicesRoute />} />
      <Route path="/invoices/:id" element={<InvoiceDetailRoute />} />
      <Route path="/transactions" element={<TransactionsRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// Route components
const CreditManagementRoute: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { data: permissionUser } = usePermissionCurrentUser();
  const getUserRole = () => {
    if (permissionUser) {
      return getAppViewRole('credit_management', permissionUser.roles, permissionUser.keycloakRoles);
    }
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  return (
    <RouteWrapper showMainNavigation={false} currentView="credit-management">
      <CreditManagementApp onBackToHome={() => navigate('/')} userRole={getUserRole()} />
    </RouteWrapper>
  );
};

const CyberSweepRoute: React.FC<{
  routeView?: 'audit-templates' | 'audit-template-detail' | 'website-configuration' | 'website-configuration-detail';
}> = ({ routeView }) => {
  const navigate = useNavigate();
  const { clientId, auditId, templateId, websiteId } = useParams<{
    clientId?: string;
    auditId?: string;
    templateId?: string;
    websiteId?: string;
  }>();
  const { user, hasRole } = useAuth();
  const { data: permissionUser } = usePermissionCurrentUser();
  const { canAccess, isLoading } = useCanAccessApp('cybersweep');
  const getUserRole = () => {
    if (permissionUser) {
      return getAppViewRole('cybersweep', permissionUser.roles, permissionUser.keycloakRoles);
    }
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cs_auditor')) return 'cs_auditor';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
      if (hasRole('data_analyst')) return 'data_analyst';
    }
    return getMVPDefaultRole();
  };

  if (!shouldBypassRoleChecks() && !isLoading && permissionUser && permissionUser.roles.length === 0 && !canAccess) {
    return (
      <RouteWrapper useMinimalHeader={true} appTitle="CyberSweep" showMainNavigation={false} currentView="app-cybersweep">
        <NoRoleAssigned />
      </RouteWrapper>
    );
  }

  if (!shouldBypassRoleChecks() && !isLoading && !canAccess) {
    return (
      <RouteWrapper useMinimalHeader={true} appTitle="CyberSweep" showMainNavigation={false} currentView="app-cybersweep">
        <AccessDenied appName="CyberSweep" />
      </RouteWrapper>
    );
  }

  return (
    <RouteWrapper
      useMinimalHeader={true}
      appTitle="CyberSweep"
      showMainNavigation={false}
      currentView="app-cybersweep"
    >
      <CyberSweepApp
        onBackToHome={() => navigate('/')}
        userRole={getUserRole()}
        urlClientId={clientId}
        urlAuditId={auditId}
        urlTemplateId={templateId}
        urlWebsiteId={websiteId}
        urlView={routeView}
      />
    </RouteWrapper>
  );
};

const CreditBlockRoute: React.FC = () => {
  const navigate = useNavigate();
  const { tab, clientId, caseId, taskId } = useParams<{
    tab?: string;
    clientId?: string;
    caseId?: string;
    taskId?: string;
  }>();
  const { user, hasRole } = useAuth();
  const { data: permissionUser } = usePermissionCurrentUser();
  const { canAccess, isLoading } = useCanAccessApp('credit_block');
  const getUserRole = () => {
    if (permissionUser) {
      return getAppViewRole('credit_block', permissionUser.roles, permissionUser.keycloakRoles);
    }
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cs_auditor')) return 'cs_auditor';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
      if (hasRole('data_analyst')) return 'data_analyst';
    }
    return getMVPDefaultRole();
  };

  if (!shouldBypassRoleChecks() && !isLoading && permissionUser && permissionUser.roles.length === 0 && !canAccess) {
    return (
      <RouteWrapper useMinimalHeader={true} appTitle="Credit Block" showMainNavigation={false} currentView="app-credit-block">
        <NoRoleAssigned />
      </RouteWrapper>
    );
  }

  if (!shouldBypassRoleChecks() && !isLoading && !canAccess) {
    return (
      <RouteWrapper useMinimalHeader={true} appTitle="Credit Block" showMainNavigation={false} currentView="app-credit-block">
        <AccessDenied appName="Credit Block" />
      </RouteWrapper>
    );
  }

  return (
    <RouteWrapper
      useMinimalHeader={true}
      appTitle="Credit Block"
      showMainNavigation={false}
      currentView="app-credit-block"
    >
      <CreditBlockApp
        onBackToHome={() => navigate('/')}
        userRole={getUserRole()}
        initialTab={tab}
        urlClientId={clientId}
        urlCaseId={caseId}
        urlTaskId={taskId}
      />
    </RouteWrapper>
  );
};

const UserManagementRoute: React.FC = () => (
  <RouteWrapper currentView="user-management">
    <UserManagement />
  </RouteWrapper>
);

const UIConfigurationRoute: React.FC = () => {
  const { user, hasRole } = useAuth();
  const getUserRole = () => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  return (
    <RouteWrapper currentView="ui-configuration">
      <UIConfigurationManager userRole={getUserRole()} />
    </RouteWrapper>
  );
};

const DynamicFormTestRoute: React.FC = () => (
  <RouteWrapper currentView="dynamic-form-test">
    <DynamicFormSectionTest />
  </RouteWrapper>
);

const ProfileRoute: React.FC = () => {
  const { user, hasRole } = useAuth();
  const getUserRole = () => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  return (
    <RouteWrapper currentView="profile">
      <UserProfile userRole={getUserRole()} />
    </RouteWrapper>
  );
};

const SettingsRoute: React.FC = () => {
  const { user, hasRole } = useAuth();
  const getUserRole = () => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  return (
    <RouteWrapper currentView="settings">
      <Settings userRole={getUserRole()} />
    </RouteWrapper>
  );
};

const SupportTicketsRoute: React.FC = () => {
  const { user, hasRole } = useAuth();
  const getUserRole = () => {
    if (user) {
      if (hasRole('system_admin')) return 'system_admin';
      if (hasRole('cs_supervisor')) return 'cs_supervisor';
      if (hasRole('cs_team')) return 'cs_team';
      if (hasRole('cb_supervisor')) return 'cb_supervisor';
      if (hasRole('cb_team')) return 'cb_team';
      if (hasRole('cm_supervisor')) return 'cm_supervisor';
      if (hasRole('cm_team')) return 'cm_team';
    }
    return getMVPDefaultRole();
  };
  return (
    <RouteWrapper currentView="support-tickets">
      <SupportTickets userRole={getUserRole()} currentUser="Current User" />
    </RouteWrapper>
  );
};

const AdminRoute: React.FC = () => {
  return (
    <RouteWrapper currentView="system-admin">
      <AdminPage />
    </RouteWrapper>
  );
};

const ClientsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="clients">
      <ClientListView
        onNavigateToClient={(clientId) => navigate(`/clients/${clientId}`)}
        onCreateClient={() => console.log('Create new client')}
      />
    </RouteWrapper>
  );
};

const ClientDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="clients">
      <ClientDetailPage id={id!} onBack={() => navigate('/clients')} />
    </RouteWrapper>
  );
};

const LeadsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="leads">
      <LeadListView
        onNavigateToLead={(leadId) => navigate(`/leads/${leadId}`)}
        onCreateLead={() => console.log('Create new lead')}
      />
    </RouteWrapper>
  );
};

const LeadDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="leads">
      <LeadDetailPage id={id!} onBack={() => navigate('/leads')} />
    </RouteWrapper>
  );
};

const OpportunitiesRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="opportunities">
      <OpportunityListView
        onNavigateToOpportunity={(opportunityId) => navigate(`/opportunities/${opportunityId}`)}
        onCreateOpportunity={() => console.log('Create new opportunity')}
      />
    </RouteWrapper>
  );
};

const OpportunityDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="opportunities">
      <OpportunityDetailPage id={id!} onBack={() => navigate('/opportunities')} />
    </RouteWrapper>
  );
};

const ContactsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="contacts">
      <ContactListView
        onNavigateToContact={(contactId) => navigate(`/contacts/${contactId}`)}
        onCreateContact={() => console.log('Create new contact')}
      />
    </RouteWrapper>
  );
};

const ContactDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="contacts">
      <ContactDetailPage
        id={id!}
        onBack={() => navigate('/contacts')}
        onNavigateToClient={(clientId) => navigate(`/clients/${clientId}`)}
      />
    </RouteWrapper>
  );
};

const CasesRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="cases">
      <CaseListView
        onNavigateToCase={(caseId) => navigate(`/cases/${caseId}`)}
        onCreateCase={() => console.log('Create new case')}
      />
    </RouteWrapper>
  );
};

const CaseDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="cases">
      <CaseDetailPage
        id={id!}
        onBack={() => navigate('/cases')}
        onNavigateToClient={(clientId) => navigate(`/clients/${clientId}`)}
        onNavigateToContact={(contactId) => navigate(`/contacts/${contactId}`)}
      />
    </RouteWrapper>
  );
};

const EventsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="events">
      <EventListView
        onNavigateToEvent={(eventId) => navigate(`/events/${eventId}`)}
        onCreateEvent={() => console.log('Create new event')}
      />
    </RouteWrapper>
  );
};

const EventDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="events">
      <EventDetailPage id={id!} onBack={() => navigate('/events')} />
    </RouteWrapper>
  );
};

const ReportsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="reports">
      <ReportListView
        onNavigateToReport={(reportId) => navigate(`/reports/${reportId}`)}
        onCreateReport={() => console.log('Create new report')}
      />
    </RouteWrapper>
  );
};

const ReportDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="reports">
      <ReportDetailPage id={id!} onBack={() => navigate('/reports')} />
    </RouteWrapper>
  );
};

const TasksRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="tasks">
      <TaskListView
        onNavigateToTask={(taskId) => navigate(`/tasks/${taskId}`)}
        onCreateTask={() => console.log('Create new task')}
      />
    </RouteWrapper>
  );
};

const TaskDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="tasks">
      <TaskDetailPage id={id!} onBack={() => navigate('/tasks')} />
    </RouteWrapper>
  );
};

const ProductsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="products">
      <ProductListView
        onNavigateToProduct={(productId) => navigate(`/products/${productId}`)}
      />
    </RouteWrapper>
  );
};

const ProductDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="products">
      <ProductDetailPage id={id!} onBack={() => navigate('/products')} />
    </RouteWrapper>
  );
};

const AccountsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="accounts">
      <AccountListView
        onNavigateToAccount={(accountId) => navigate(`/accounts/${accountId}`)}
      />
    </RouteWrapper>
  );
};

const AccountDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="accounts">
      <AccountDetailPage id={id!} onBack={() => navigate('/accounts')} />
    </RouteWrapper>
  );
};

const PricebooksRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="pricebooks">
      <PricebookListView
        onNavigateToPricebook={(pricebookId) => navigate(`/pricebooks/${pricebookId}`)}
      />
    </RouteWrapper>
  );
};

const PricebookDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="pricebooks">
      <PricebookDetailPage id={id!} onBack={() => navigate('/pricebooks')} />
    </RouteWrapper>
  );
};

const InvoicesRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="invoices">
      <InvoiceListView
        onNavigateToInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
      />
    </RouteWrapper>
  );
};

const InvoiceDetailRoute: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="invoices">
      <InvoiceDetailPage id={id!} onBack={() => navigate('/invoices')} />
    </RouteWrapper>
  );
};

const TransactionsRoute: React.FC = () => {
  const navigate = useNavigate();
  return (
    <RouteWrapper currentView="transactions">
      <InvoiceListView
        pageTitle="Transactions"
        pageSubtitle="View all transactions"
        searchPlaceholder="Search transactions..."
        defaultViewName="All Transactions"
        exportFilenamePrefix="Transactions"
        onNavigateToInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
      />
    </RouteWrapper>
  );
};
